import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AlertBox from '../components/AlertBox';
import { API_BASE_URL, STRIPE_PUBLISHABLE_KEY } from '../config';

interface Event {
  id: number;
  uuid: string;
  title: string;
  description: string;
  event_start_date: string;
  event_end_date: string;
  event_address: string;
  event_ticket_price_adults: number;
  event_ticket_price_kids: number;
  discount_allowed: boolean;
  discount_value: number;
  is_disabled: boolean;
  image_files: string[];
}

const PublicBooking: React.FC = () => {
  const { eventUuid } = useParams<{ eventUuid: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Two-step booking state
  const [bookingStep, setBookingStep] = useState<1 | 2>(1);
  const [verifiedEmail, setVerifiedEmail] = useState<string>('');
  const [verifiedName, setVerifiedName] = useState<string>('');
  const [pinVerified, setPinVerified] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    pin: '',
    adults: 0,
    kids: 0,
    mode: 'stripe' as 'stripe'
  });

  const [bookingReference, setBookingReference] = useState<string | null>(null);
  const [existingBookingReference, setExistingBookingReference] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadEvent();
    
    // Check if returning from Stripe Checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (canceled === 'true') {
      navigate(`/booking-error?reason=canceled&event=${eventUuid}`);
      return;
    } else if (success === 'true' && sessionId) {
      handleStripeReturn(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUuid]);

  const handleStripeReturn = async (sessionId: string) => {
    try {
      setSubmitting(true);
      console.log('=== Stripe Return Handler ===');
      console.log('Session ID:', sessionId);
      console.log('Event UUID:', eventUuid);
      
      setSuccess('Payment successful! Creating your booking...');
      
      // Call backend to complete the booking
      console.log('Calling complete-stripe-booking endpoint...');
      const response = await fetch(`${API_BASE_URL}/public-tickets/complete-stripe-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          event_uuid: eventUuid
        })
      });

      const data = await response.json();
      console.log('Complete booking response:', data);

      if (!response.ok) {
        console.error('Failed to complete booking:', data.error);
        // Payment went through but booking creation failed — user may have been charged
        const reason = response.status >= 500 ? 'booking_creation' : 'payment_processing';
        navigate(
          `/booking-error?reason=${reason}&event=${eventUuid}` +
            (data.error ? `&msg=${encodeURIComponent(data.error)}` : '')
        );
        return;
      }

      console.log('Booking completed successfully!');
      console.log('Booking reference:', data.booking.booking_reference);
      console.log('Is new booking:', data.isNewBooking);
      
      // If booking already exists, redirect to manage booking page
      if (data.isNewBooking === false) {
        console.log('Existing booking detected, redirecting to manage booking page...');
        navigate(`/manage-booking/${data.booking.booking_reference}`);
        return;
      }
      
      setBookingReference(data.booking.booking_reference);
      setSuccess('Payment confirmed! Your booking is complete.');
      
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err: any) {
      console.error('=== ERROR in handleStripeReturn ===');
      console.error('Error:', err);
      // Network/unexpected error — almost certainly not charged
      navigate(
        `/booking-error?reason=payment_processing&event=${eventUuid}` +
          (err.message ? `&msg=${encodeURIComponent(err.message)}` : '')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/public-tickets/events`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched events:', data);
      console.log('Looking for event UUID:', eventUuid);
      console.log('Available event UUIDs:', data.events?.map((e: any) => e.uuid));
      
      if (!data.events || !Array.isArray(data.events)) {
        throw new Error('Invalid response format');
      }
      
      const foundEvent = data.events.find((e: Event) => e.uuid === eventUuid);
      
      if (!foundEvent) {
        console.error('Event not found. Available events:', data.events.map((e: any) => ({ uuid: e.uuid, title: e.title })));
        setError('Event not found or is not available for booking');
        return;
      }

      const eventStartDate = new Date(foundEvent.event_start_date);

      if (foundEvent.is_disabled) {
        setError('This event is not available for booking.');
        return;
      }

      // Check if event is in the future
      if (eventStartDate <= new Date()) {
        setError('This event has already passed. Tickets are no longer available.');
        return;
      }

      setEvent(foundEvent);
    } catch (err: any) {
      console.error('Error loading event:', err);
      setError(err.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Submit name and email to get PIN
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setExistingBookingReference(null);

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/public-tickets/initiate-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_uuid: eventUuid,
          customer_name: formData.customer_name,
          email: formData.email
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initiate booking');
      }

      // Check if user already has a booking
      if (data.hasExistingBooking) {
        setExistingBookingReference(data.booking_reference);
        setError('You already have a booking for this event.');
        return;
      }

      // PIN sent successfully
      setSuccess('A 6-digit verification code has been sent to your email. Please check your inbox — and your Spam / Junk folder if you don\'t see it.');
      setBookingStep(2);
      setVerifiedEmail(formData.email);
      setVerifiedName(formData.customer_name);
    } catch (err: any) {
      console.error('Error initiating booking:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Verify PIN and proceed with ticket selection
  const handleVerifyPIN = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/public-tickets/verify-booking-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_uuid: eventUuid,
          email: verifiedEmail,
          pin: formData.pin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      setPinVerified(true);
      setSuccess('✓ Verification successful! Please select your tickets below.');
      // PIN verified, user can now continue with the booking form
    } catch (err: any) {
      console.error('Error verifying PIN:', err);
      setError(err.message || 'Failed to verify code');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    if (!event) return { original: 0, discount: 0, final: 0 };

    const original = 
      (formData.adults * (event.event_ticket_price_adults || 0)) + 
      (formData.kids * (event.event_ticket_price_kids || 0));
    
    let discount = 0;
    if (event.discount_allowed && event.discount_value > 0) {
      discount = (original * event.discount_value) / 100;
    }

    const final = original - discount;

    return { original, discount, final };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== PublicBooking: Form Submit ===');
    console.log('Form Data:', formData);
    console.log('Event UUID:', eventUuid);
    console.log('Event:', event);
    console.log('Verified Email:', verifiedEmail);
    console.log('Verified Name:', verifiedName);
    
    setError(null);
    setSuccess(null);

    // Validate that PIN was verified
    if (!pinVerified) {
      setError('Please verify your PIN first');
      return;
    }

    // Validate verified email and name exist
    if (!verifiedEmail || !verifiedName) {
      setError('Missing contact information. Please start over.');
      setBookingStep(1);
      setPinVerified(false);
      return;
    }

    if (formData.adults === 0 && formData.kids === 0) {
      console.error('Validation failed: No tickets selected');
      setError('Please select at least one ticket');
      return;
    }
    
    console.log('Validation passed, proceeding with booking...');

    try {
      setSubmitting(true);

      // For Stripe payments, redirect to Stripe Checkout
      if (formData.mode === 'stripe') {
        console.log('Payment mode: Stripe');
        console.log('Creating checkout session...');
        
        const checkoutPayload = {
          event_uuid: eventUuid,
          customer_name: verifiedName,
          email: verifiedEmail,
          adults: formData.adults,
          kids: formData.kids
        };
        console.log('Checkout payload:', checkoutPayload);
        
        // Create checkout session
        const checkoutResponse = await fetch(`${API_BASE_URL}/public-tickets/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(checkoutPayload)
        });

        const checkoutData = await checkoutResponse.json();
        console.log('Checkout response status:', checkoutResponse.ok);
        console.log('Checkout data:', checkoutData);

        if (!checkoutResponse.ok) {
          console.error('Checkout creation failed:', checkoutData.error);
          console.error('Checkout validation errors:', checkoutData.errors);
          
          // Show validation errors if available
          if (checkoutData.errors && Array.isArray(checkoutData.errors)) {
            const errorMessages = checkoutData.errors.map((e: any) => e.msg).join(', ');
            throw new Error(errorMessages);
          }
          
          // Check if user already has a booking
          if (checkoutData.existingBooking && checkoutData.booking_reference) {
            setError('You already have a booking for this event. Redirecting to your booking...');
            setTimeout(() => {
              navigate(`/manage-booking/${checkoutData.booking_reference}`);
            }, 2000);
            return;
          }
          
          throw new Error(checkoutData.error || 'Failed to create checkout session');
        }
        
        console.log('Checkout session created, redirecting to Stripe...');

        // Redirect to Stripe Checkout
        const stripe = (window as any).Stripe(STRIPE_PUBLISHABLE_KEY);
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: checkoutData.sessionId
        });

        if (stripeError) {
          throw new Error(stripeError.message || 'Failed to redirect to checkout');
        }

        // Note: User will be redirected away from this page
        // They'll return via the success_url with session_id parameter
        return;
      } else {
        console.log('Payment mode:', formData.mode);
        console.log('Creating booking directly (stripe)...');
        
        const bookingPayload = {
          event_uuid: eventUuid,
          customer_name: verifiedName,
          email: verifiedEmail,
          adults: formData.adults,
          kids: formData.kids,
          mode: formData.mode
        };
        console.log('Booking payload:', bookingPayload);
        
        // For cash/interac payments, just create the booking
        const response = await fetch(`${API_BASE_URL}/public-tickets/book`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingPayload)
        });

        const data = await response.json();
        console.log('Booking response status:', response.ok);
        console.log('Booking response data:', data);

        if (!response.ok) {
          console.error('Booking creation failed:', data.error);
          
          // Check if user already has a booking
          if (data.existingBooking && data.booking_reference) {
            setError('You already have a booking for this event. Redirecting to your booking...');
            setTimeout(() => {
              navigate(`/manage-booking/${data.booking_reference}`);
            }, 2000);
            return;
          }
          
          throw new Error(data.error || 'Failed to create booking');
        }

        console.log('Booking created successfully!');
        console.log('Booking reference:', data.booking.booking_reference);
        setBookingReference(data.booking.booking_reference);
        setSuccess('Booking created successfully!');
      }
      
      // Clear form
      setFormData({
        customer_name: '',
        email: '',
        pin: '',
        adults: 0,
        kids: 0,
        mode: 'stripe'
      });
    } catch (err: any) {
      console.error('=== ERROR creating booking ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to create booking');
    } finally {
      console.log('Form submission complete, resetting submitting state');
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'adults' || name === 'kids' ? parseInt(value) || 0 : value
    }));
  };

  const totals = calculateTotal();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <AlertBox type="error" message={error} />
          <button
            onClick={() => navigate('/events')}
            className="mt-4 w-full bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white py-2 px-4 rounded-lg transition"
          >
            View All Events
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Event Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}</h1>
          <div className="space-y-2 text-gray-600">
            <p><strong>Date:</strong> {new Date(event.event_start_date).toLocaleString()}</p>
            <p><strong>Location:</strong> {event.event_address}</p>
            {event.description && <p><strong>Description:</strong> {event.description}</p>}
          </div>
          
          {/* Ticket Prices */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-warm-50 to-primary-100 p-4 rounded-lg border border-warm-200">
              <p className="text-sm text-secondary-600">Adult Ticket</p>
              <p className="text-2xl font-bold text-primary-700">
                ${event.event_ticket_price_adults?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-warm-50 to-primary-100 p-4 rounded-lg border border-warm-200">
              <p className="text-sm text-secondary-600">Kids Ticket</p>
              <p className="text-2xl font-bold text-primary-700">
                ${event.event_ticket_price_kids?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>

          {event.discount_allowed && event.discount_value > 0 && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-semibold">
                🎉 {event.discount_value}% Discount Available!
              </p>
            </div>
          )}
        </div>

        {/* Booking Reference Display */}
        {bookingReference && (
          <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-green-800 mb-2">Booking Confirmed!</h2>
            <p className="text-gray-700 mb-4">
              Your booking reference is:
            </p>
            <div className="bg-white border-2 border-green-600 rounded-lg p-4 mb-4">
              <p className="text-4xl font-mono font-bold text-center text-green-700 tracking-widest">
                {bookingReference}
              </p>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Please save this reference number. You can use it to view, edit, or cancel your booking.
            </p>
            <button
              onClick={() => navigate(`/manage-booking/${bookingReference}`)}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition"
            >
              Manage This Booking
            </button>
          </div>
        )}

        {/* Existing Booking Notification */}
        {existingBookingReference && !bookingReference && (
          <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-blue-800 mb-2">Booking Already Exists!</h2>
            <p className="text-gray-700 mb-4">
              You already have a booking for this event. Use your booking reference below to manage it:
            </p>
            <div className="bg-white border-2 border-blue-600 rounded-lg p-4 mb-4">
              <p className="text-3xl font-mono font-bold text-center text-blue-700 tracking-widest">
                {existingBookingReference}
              </p>
            </div>
            <button
              onClick={() => navigate(`/manage-booking/${existingBookingReference}`)}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
            >
              Manage Your Booking
            </button>
          </div>
        )}

        {/* Booking Form */}
        {!bookingReference && !existingBookingReference && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Book Your Tickets</h2>

            {/* Step Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-center space-x-4">
                <div className={`flex items-center ${bookingStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bookingStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
                    {bookingStep > 1 ? '✓' : '1'}
                  </div>
                  <span className="ml-2 text-sm font-medium">Contact Info</span>
                </div>
                <div className={`w-16 h-1 ${bookingStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
                <div className={`flex items-center ${bookingStep >= 2 && pinVerified ? 'text-green-600' : bookingStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bookingStep >= 2 && pinVerified ? 'bg-green-600 text-white' : bookingStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
                    {pinVerified ? '✓' : '2'}
                  </div>
                  <span className="ml-2 text-sm font-medium">{pinVerified ? 'Verified' : 'Verify & Book'}</span>
                </div>
              </div>
            </div>

            {error && <AlertBox type="error" message={error} />}
            {success && <AlertBox type="success" message={success} />}

            {/* Step 1: Contact Information */}
            {bookingStep === 1 && (
              <form onSubmit={handleStep1Submit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    We'll send a 6-digit verification code to this email. If you don't see it in your inbox, please check your <strong>Spam / Junk</strong> folder.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white py-3 px-6 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending Code...' : 'Continue'}
                </button>
              </form>
            )}

            {/* Step 2: Verify PIN and Select Tickets */}
            {bookingStep === 2 && (
              <div className="space-y-6">
                {/* Show verified email */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>Email:</strong> {verifiedEmail}
                    <button
                      onClick={() => {
                        setBookingStep(1);
                        setPinVerified(false);
                        setSuccess(null);
                        setError(null);
                      }}
                      className="ml-2 text-primary-600 hover:text-primary-700 text-xs underline"
                    >
                      Change
                    </button>
                  </p>
                </div>

                {/* PIN Verification Form */}
                {!pinVerified && (
                  <form onSubmit={handleVerifyPIN} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter 6-Digit Verification Code *
                      </label>
                      <input
                        type="text"
                        name="pin"
                        value={formData.pin}
                        onChange={handleChange}
                        required
                        maxLength={6}
                        pattern="[0-9]{6}"
                        autoComplete="off"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                        placeholder="000000"
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        Check your email for the verification code (valid for 15 minutes)
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || formData.pin.length !== 6}
                      className="w-full bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white py-3 px-6 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Verifying...' : 'Verify Code'}
                    </button>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          setError(null);
                          setSuccess(null);
                          try {
                            setSubmitting(true);
                            const response = await fetch(`${API_BASE_URL}/public-tickets/initiate-booking`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                event_uuid: eventUuid,
                                customer_name: verifiedName,
                                email: verifiedEmail
                              })
                            });
                            const data = await response.json();
                            if (response.ok) {
                              setSuccess('New verification code sent to your email!');
                              setFormData({ ...formData, pin: '' });
                            } else {
                              setError(data.error || 'Failed to resend code');
                            }
                          } catch (err: any) {
                            setError(err.message || 'Failed to resend code');
                          } finally {
                            setSubmitting(false);
                          }
                        }}
                        disabled={submitting}
                        className="flex-1 text-primary-600 hover:text-primary-700 py-2 text-sm font-medium border border-primary-300 hover:border-primary-400 rounded-lg transition disabled:opacity-50"
                      >
                        Resend Code
                      </button>

                    <button
                      type="button"
                      onClick={() => {
                        setBookingStep(1);
                        setPinVerified(false);
                        setFormData({ ...formData, pin: '' });
                        setSuccess(null);
                        setError(null);
                      }}
                      className="flex-1 text-gray-600 hover:text-gray-800 py-2 text-sm font-medium border border-gray-300 hover:border-gray-400 rounded-lg transition"
                    >
                      ← Change Email
                    </button>
                    </div>
                  </form>
                )}

                {/* Ticket Selection Form - Shows after PIN verification */}
                {pinVerified && (
                  <>
                    {/* Divider */}
                    <div className="border-t-2 border-green-200 pt-6 mt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">✓</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Email Verified!</h3>
                          <p className="text-sm text-gray-600">Now select your tickets and complete payment</p>
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Ticket Selection */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 mb-3">
                          Adult Tickets
                        </label>
                        <div className="flex items-center gap-2 bg-gradient-to-r from-warm-50 to-primary-100 p-3 rounded-xl border-2 border-warm-300 shadow-sm">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, adults: Math.max(0, prev.adults - 1) }))}
                            disabled={formData.adults === 0}
                            className="w-12 h-12 flex items-center justify-center bg-white hover:bg-warm-100 text-warm-700 rounded-lg font-bold text-2xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed border-2 border-warm-300"
                          >
                            −
                          </button>
                          <div className="flex-1 text-center bg-white rounded-lg py-2 shadow-inner border-2 border-warm-200">
                            <span className="text-3xl font-bold text-primary-700">{formData.adults}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, adults: prev.adults + 1 }))}
                            className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white rounded-lg font-bold text-2xl transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 mb-3">
                          Kids Tickets
                        </label>
                        <div className="flex items-center gap-2 bg-gradient-to-r from-warm-50 to-primary-100 p-3 rounded-xl border-2 border-warm-300 shadow-sm">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, kids: Math.max(0, prev.kids - 1) }))}
                            disabled={formData.kids === 0}
                            className="w-12 h-12 flex items-center justify-center bg-white hover:bg-warm-100 text-warm-700 rounded-lg font-bold text-2xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed border-2 border-warm-300"
                          >
                            −
                          </button>
                          <div className="flex-1 text-center bg-white rounded-lg py-2 shadow-inner border-2 border-warm-200">
                            <span className="text-3xl font-bold text-primary-700">{formData.kids}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, kids: prev.kids + 1 }))}
                            className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white rounded-lg font-bold text-2xl transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method *
                      </label>
                      <select
                        name="mode"
                        value={formData.mode}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="stripe">Credit/Debit Card (Stripe)</option>
                      </select>
                    </div>

                    {/* Total Calculation */}
                    {(formData.adults > 0 || formData.kids > 0) && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-gray-700">
                          <span>Subtotal:</span>
                          <span className="font-semibold">${totals.original.toFixed(2)}</span>
                        </div>
                        
                        {totals.discount > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({event?.discount_value}%):</span>
                            <span className="font-semibold">-${totals.discount.toFixed(2)}</span>
                          </div>
                        )}
                        
                        <div className="pt-2 border-t border-gray-300 flex justify-between text-lg font-bold text-primary-700">
                          <span>Total:</span>
                          <span>${totals.final.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting || (formData.adults === 0 && formData.kids === 0)}
                      className="w-full bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white py-3 px-6 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Processing...' : (formData.adults === 0 && formData.kids === 0) ? 'Select at least one ticket' : 'Book Tickets'}
                    </button>

                    <p className="text-sm text-gray-500 text-center">
                      * You will receive a booking reference to manage your tickets
                    </p>
                  </form>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Manage Booking Link */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 mb-2">Already have a booking?</p>
          <button
            onClick={() => navigate('/manage-booking')}
            className="text-primary-600 hover:text-primary-700 font-semibold"
          >
            Manage Your Booking →
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;
